import { Box, Stack, Typography } from '@atgfr8/atg-ui-tailwind';
import { DeleteIcon, NoteIcon } from '@atgfr8/icons';
import { ToastType } from '@components';
import { Button } from '@components/Button';
import GlassModal from '@mobileComponents/GlassModal';
import SectionCard from '@mobileComponents/SectionCard';
import {
  customerDetailsStore,
  deleteCustomerGeneralComment,
  updateCustomerDetailsState,
} from '@mobileStore/customer';
import { mobileUserStore } from '@mobileStore/user';
import { mobileIsAdmin, mobileIsSuperAdmin } from '@mobileUtils/roleUtils';
import { Comments } from '@typeDefinitions/comments';
import { handleToast } from '@utils/utils';
import { DateTime } from 'luxon';
import { createSignal, For } from 'solid-js';

import AddComment from './AddComment';

type CommentProps = {
  comments: Comments[];
  type: 'Customer' | 'Order' | 'Lead';
};

const isAdminOrOriginalAuthor = (comment: Comments) => {
  if (mobileIsAdmin()) return true;
  if (mobileIsSuperAdmin()) return true;
  if (comment.userId === mobileUserStore.user.id) return true;
  return false;
};

const CommentsCard = (props: CommentProps) => {
  const [isDeleteModalOpen, setIsDeleteModalOpen] = createSignal(false);
  const [commentToDelete, setCommentToDelete] = createSignal<number | null>(
    null,
  );
  const [isDeleting, setIsDeleting] = createSignal(false);

  const handleDeleteClick = (commentId: number) => {
    setCommentToDelete(commentId);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (commentToDelete() === null) return;
    setIsDeleting(true);
    const res = await deleteCustomerGeneralComment(commentToDelete()!);
    if (res) {
      updateCustomerDetailsState({
        comments: customerDetailsStore.customer.comments.filter(
          (comment) => comment.id !== commentToDelete(),
        ),
      });
      handleToast(ToastType.Success, 'Comment deleted successfully');
    }
    setIsDeleting(false);
    setIsDeleteModalOpen(false);
    setCommentToDelete(null);
  };

  const handleDeleteCancel = () => {
    setIsDeleteModalOpen(false);
    setCommentToDelete(null);
  };

  return (
    <>
      <SectionCard
        title="COMMENTS"
        startIcon={<NoteIcon />}
        endIcon={<AddComment />}
      >
        {props.comments.length > 0 ? (
          <Stack spacing={0.5} class="p-2!">
            <For each={props.comments}>
              {(comment: Comments) => (
                <Box class="border border-[#026EA180] rounded-sm">
                  <Box class="flex justify-between items-center bg-[#026EA11F]! h-[35px] p-1 border-b border-[#026EA180]">
                    <Typography
                      variant="subtitle2"
                      class="font-semibold text-[14px]!"
                    >
                      {comment.name ?? '-'}
                    </Typography>
                    <Box class="flex items-center gap-2">
                      <Typography
                        variant="caption"
                        class="text-right text-[14px]!"
                      >
                        {DateTime.fromISO(comment.timestamp as string, {
                          zone: 'utc',
                        })
                          .setZone('local')
                          .toFormat('dd/MM/yyyy hh:mm a')}
                      </Typography>
                      {isAdminOrOriginalAuthor(comment) && (
                        <DeleteIcon
                          color="#B00020"
                          style={{ cursor: 'pointer' }}
                          size={20}
                          onClick={() => handleDeleteClick(comment.id)}
                        />
                      )}
                    </Box>
                  </Box>

                  <Typography
                    class="whitespace-pre-wrap wrap-break-word text-[14px]! p-1"
                    variant="body2"
                  >
                    {comment.comment}
                  </Typography>
                </Box>
              )}
            </For>
          </Stack>
        ) : (
          <Box class="text-[14px]! p-[10px]">No Comments to display</Box>
        )}
      </SectionCard>

      <GlassModal
        isOpen={isDeleteModalOpen()}
        onClose={handleDeleteCancel}
        title="Delete Comment"
        maxWidth="sm"
      >
        <div class="space-y-3">
          <p>Are you sure you want to delete this comment?</p>
          <div class="flex justify-end gap-2">
            <Button
              onClick={handleDeleteCancel}
              variant="text"
              label="CANCEL"
              dataTestId="delete-comment-cancel-button"
              disabled={isDeleting()}
            />
            <Button
              onClick={handleDeleteConfirm}
              variant="contained"
              label="DELETE"
              class="bg-[#b00020]!"
              isLoading={isDeleting()}
              dataTestId="delete-comment-confirm-button"
              disabled={isDeleting()}
            />
          </div>
        </div>
      </GlassModal>
    </>
  );
};

export default CommentsCard;
